import { type FC, type MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { parse } from 'querystring';

import { useQueryClient } from '@tanstack/react-query';
import { StructuredData } from '@vakantiesnl/components/src/__LEGACY__/atoms/StructuredData';
import { BrandedSearchFooterBlock } from '@vakantiesnl/components/src/__LEGACY__/blocks/branded-search-footer';
import { Content } from '@vakantiesnl/components/src/atoms/Content';
import { Divider } from '@vakantiesnl/components/src/atoms/Divider';
import { Drawer } from '@vakantiesnl/components/src/atoms/Drawer';
import { BrandedSearchRelatedCards } from '@vakantiesnl/components/src/molecules/BrandedSearchRelatedCards';
import { BreadCrumbs, formatGeoBreadcrumbs } from '@vakantiesnl/components/src/molecules/BreadCrumbs';
import { FilterList } from '@vakantiesnl/components/src/molecules/FilterList';
import { FiltersWrapper } from '@vakantiesnl/components/src/molecules/FiltersWrapper';
import { Reviews } from '@vakantiesnl/components/src/organisms/Reviews';
import { getGeoEntitiesFromSlugs, isSingleGeoSelected } from '@vakantiesnl/components/src/utils/geo';
import { useSearchMeta } from '@vakantiesnl/components/src/utils/search/useSearchMeta';
import { useBreakpoints } from '@vakantiesnl/components/src/utils/useBreakpoints';
import { useMicroCopyContext } from '@vakantiesnl/services/src/context/microCopyContext';
import { useContentfulIdContext } from '@vakantiesnl/services/src/context/useContentfulIdContext';
import { useSeoContext } from '@vakantiesnl/services/src/context/useSeoContext';
import { useSearchPageEntity } from '@vakantiesnl/services/src/gtm/util/useSearchPageEntity';
import { useBrandedSearchTemplate } from '@vakantiesnl/services/src/hooks/contentful';
import { useParseDurationsFilterConfig } from '@vakantiesnl/services/src/hooks/filter-hooks';
import { getSearchPageState, useGetGeo, useGetZooverCompositeSearch } from '@vakantiesnl/services/src/hooks/queries';
import { useGetReviews } from '@vakantiesnl/services/src/hooks/queries/useGetReviews';
import { useSearchUrlBuilder } from '@vakantiesnl/services/src/hooks/useSearchUrlBuilder';
import { useScrollDirection } from '@vakantiesnl/services/src/hooks/utils';
import { useApplicationStore } from '@vakantiesnl/services/src/stores/applicationStore';
import {
	type RemoveFilterKey,
	useFilterStore,
	getSelectedChainsBySlugs,
	GEO_SLUGS_FILTER_KEYS,
	getSelectedTourOperatorsBySlugs,
	getSelectedDurations,
	isDefaultDurations,
} from '@vakantiesnl/services/src/stores/filtersStore';
import { useGlobalFiltersStore } from '@vakantiesnl/services/src/stores/globalFiltersStore';
import { getSelectedGeoFromSlugs } from '@vakantiesnl/services/src/util/geo';
import { getRobotsOverride } from '@vakantiesnl/services/src/util/robots';
import { type Search } from '@vakantiesnl/types';
import { type MappedDestinationView } from '@vakantiesnl/types/src/geo';
import { useRouter } from 'next/router';
import { InView } from 'react-intersection-observer';
import { Element, scroller } from 'react-scroll';
import useDeepCompareEffect from 'use-deep-compare-effect';

import { AccommodationList } from './AccommodationList';
import { FlashDealSearchList } from './FlashDealSearchList';
import { useStyles } from './SearchResults.styles';
import { FilterAndSortBar } from '../../molecules/FilterAndSortBar';
import { FiltersLabels } from '../../molecules/FilterLabelList';
import { type OptionProp } from '../../molecules/FilterList/FilterList.types';
import { FiltersControls } from '../../molecules/FiltersControls';
import { getCitySchemaItem, getReviewSchemaItem, updatePartyCompositionStore } from '../../utils';
import { BrandedSearchHeader } from '../BrandedSearchHeader';

const MAX_SEARCH_RELATED_CARDS = 4;

export const SearchResults: FC = () => {
	const scrollDirection = useScrollDirection();
	const { classes, cx } = useStyles({ scrollDirection });

	const { isDesktop } = useBreakpoints();
	const microCopy = useMicroCopyContext();
	const { setSeo } = useSeoContext();
	const { setContentfulId } = useContentfulIdContext();
	const readMoreRef = useRef<HTMLDivElement>(null);
	const [showDialog, setShowDialog] = useState(false);
	const [isPaxBarInView, setIsPaxBarInView] = useState(false);
	const [showSortDrawer, setShowSortDrawer] = useState(false);
	const { brandedSearchTemplate } = useBrandedSearchTemplate();

	const globalFilters = useGlobalFiltersStore((s) => s.filters);
	const zustandSetPartyComposition = useGlobalFiltersStore((s) => s.setParty);

	const toggleDialog = useCallback(() => setShowDialog(!showDialog), [showDialog]);
	const geo = useGetGeo();

	const toggleSortDrawer = useCallback(() => {
		setShowSortDrawer(!showSortDrawer);
	}, [showSortDrawer]);

	const filters = useFilterStore((s) => s.filters);
	const setFilterDepartureDate = useFilterStore((s) => s.setFilterDepartureDate);
	const setDurations = useGlobalFiltersStore((s) => s.setDurations);
	const setFilterFlexibleDepartureDate = useFilterStore((s) => s.setFilterFlexibleDepartureDate);
	const removeFilter = useFilterStore((s) => s.removeFilter);
	const clearFilters = useFilterStore((s) => s.clearFilters);
	const setPage = useFilterStore((s) => s.setPage);
	const setInitState = useFilterStore((s) => s.setInitState);
	const filterCount = useFilterStore((s) => s.getSelectedFilterCount());
	const setGeoFilter = useFilterStore((s) => s.setGeoFilter);
	const setChainsFilter = useFilterStore((s) => s.setChainsFilter);
	const setTourOperatorsFilter = useFilterStore((s) => s.setTourOperatorsFilter);

	const queryClient = useQueryClient();
	const router = useRouter();
	const searchMeta = useSearchMeta(filters, globalFilters);
	const searchUrl = useSearchUrlBuilder(searchMeta, router.query);

	const { accommodations, totalResults, isLoadingAccommodations } = useGetZooverCompositeSearch();

	const entitySearch = useSearchPageEntity({
		countrySlugs: filters.countrySlugs,
		regionSlugs: filters.regionSlugs,
		citySlugs: filters.citySlugs,
		themes: filters.themes,
	});

	useEffect(() => {
		if (brandedSearchTemplate) {
			setSeo({
				...brandedSearchTemplate.seo,
				robots: getRobotsOverride(router.asPath, totalResults) || brandedSearchTemplate.seo.robots,
			});
			setContentfulId(brandedSearchTemplate.id);
		}
	}, [brandedSearchTemplate, setContentfulId, setSeo, totalResults, router.asPath]);

	const durationDayRange = useParseDurationsFilterConfig(microCopy);

	const selectedRange = durationDayRange.find((option) => {
		const durations = getSelectedDurations(globalFilters, filters);

		const rangeSelector = Array.isArray(durations)
			? durations[durations.length - 1] || durationDayRange[1].value.split(',')[durations.length - 1]
			: durations.split('-')[durations.split('-').length - 1];

		return rangeSelector && option.value.includes(String(rangeSelector));
	});
	const [durationsRange, setDurationsRange] = useState(selectedRange);

	// Update selectedRange when navigating between lister pages
	useEffect(() => {
		setDurationsRange(selectedRange);
	}, [selectedRange]);

	/** Trip duration range picker used for dropdown */
	const durationsRangeChange = useCallback(
		(option: OptionProp, setFilter: boolean): void => {
			const options = option.value.split(',');
			const firstOption = options[0];
			const lastOption = options[options.length - 1];
			setDurationsRange(option);
			if (setFilter) {
				const newValue = `${firstOption}-${lastOption}`;
				if (isDefaultDurations(newValue, filters)) {
					setDurations(null);
				} else {
					setDurations(`${firstOption}-${lastOption}`);
				}
			}
		},
		[setDurationsRange, setDurations, filters],
	);

	/** Active filter removal function used on the pills */
	const onFilterRemove = useCallback(
		(event: MouseEvent, filterType: RemoveFilterKey | 'durations', value: string) => {
			event.preventDefault();

			if (filterType === 'durations') {
				// It can only be an array as we don't show a filter pill for ranges like '6-10'
				if (!Array.isArray(globalFilters.durations)) {
					return;
				}

				const valueAsNumber = Number(value);
				const updatedDurations = globalFilters.durations.filter((d) => d !== valueAsNumber);

				// // If it'll be an empty array then fall back to the matching duration range and update the filters
				if (updatedDurations.length === 0 && durationsRange) {
					durationsRangeChange(durationsRange, true);
				} else {
					setDurations(updatedDurations);
				}

				return;
			}

			if (GEO_SLUGS_FILTER_KEYS.includes(filterType) && value && filters[filterType]) {
				/** Create a new filterstate without the removed filter to check children geo */
				const newFilterState = {
					...filters,
					[filterType]: (filters[filterType] as string[]).filter((stateFilter) => stateFilter !== value),
				};
				const { countries, regions, cities } = getSelectedGeoFromSlugs(newFilterState, geo);

				setGeoFilter({
					countries: countries.map((country) => ({ entityId: country.entityId, slug: country.slug })),
					regions: regions.map((region) => ({ entityId: region.entityId, slug: region.slug })),
					cities: cities.map((city) => ({ entityId: city.entityId, slug: city.slug })),
				});
			} else if (filterType === 'chainsSlugs') {
				/** Make a slug array without the value to remove */
				const filteredChainSlugs = filters[filterType].filter((chainSlug) => chainSlug !== value);

				/** Get the selected chains to update the store with */
				const selectedChains = getSelectedChainsBySlugs(filteredChainSlugs);

				setChainsFilter(selectedChains);
			} else if (filterType === 'tourOperatorsSlugs') {
				/** Make a slug array without the value to remove */
				const filteredTourOperatorsSlugs = filters[filterType].filter(
					(tourOperatorsSlug) => tourOperatorsSlug !== value,
				);

				/** Get the selected tourOperators to update the store with */
				const selectedChains = getSelectedTourOperatorsBySlugs(filteredTourOperatorsSlugs);

				setTourOperatorsFilter(selectedChains);
			} else removeFilter(filterType, value);
		},
		[
			globalFilters.durations,
			setDurations,
			filters,
			removeFilter,
			geo,
			setGeoFilter,
			durationsRange,
			durationsRangeChange,
			setChainsFilter,
			setTourOperatorsFilter,
		],
	);

	/** All active filters removal function */
	const onClear = useCallback(() => {
		/**  Will also reset the default duration range */
		if (durationDayRange?.length >= 1) {
			// No need to update the filters twice
			durationsRangeChange(durationDayRange[1], false);
		}
		clearFilters();
		setDurations(null);
	}, [clearFilters, durationDayRange, durationsRangeChange, setDurations]);

	const setPartyComposition = useCallback(
		(party: Search.PartyComposition[]) => {
			import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
				import('@vakantiesnl/services/src/gtm/search').then(({ onSetPartyComposition }) => {
					track(onSetPartyComposition(party, entitySearch));
				}),
			);
			zustandSetPartyComposition(party);
			setPage(1);
		},
		[entitySearch, setPage, zustandSetPartyComposition],
	);

	const setDepartureDate = useCallback(
		(date: string[]) => {
			import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
				import('@vakantiesnl/services/src/gtm/search').then(({ onSetDepartureDate }) => {
					track(onSetDepartureDate(date, entitySearch));
				}),
			);

			setFilterDepartureDate(date);
		},
		[setFilterDepartureDate, entitySearch],
	);

	const toggleDepartureOffset = useCallback(
		(checked: boolean | number) => {
			import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
				import('@vakantiesnl/services/src/gtm/search').then(({ onSetDepartureOffset }) => {
					track(onSetDepartureOffset(checked, entitySearch));
				}),
			);
			setFilterFlexibleDepartureDate(checked);
		},
		[setFilterFlexibleDepartureDate, entitySearch],
	);

	useEffect(() => {
		/** Keep router state and filters state in sync when using go-back browser button  */
		router.beforePopState((newRoute) => {
			// Use the new url as the old one is not updated yet.
			const newQuery = parse(newRoute.url.split('?')[1]);

			/** Only re-initialize the filters on search pages */
			if (newRoute.url.includes('/search')) {
				getSearchPageState(queryClient, newQuery).then((state) => {
					setInitState(state.filterState);
				});
			}

			updatePartyCompositionStore(newQuery, zustandSetPartyComposition);
			return true;
		});

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [router]);

	/** Replace the route url when searchUrl changes */
	useDeepCompareEffect(() => {
		router.push(searchUrl.href, searchUrl.as, { scroll: false, shallow: true });
	}, [searchUrl.href, searchUrl.as]);

	const { country, region, city } = useMemo(() => getGeoEntitiesFromSlugs(filters, geo), [filters, geo]);

	const formattedBreadCrumbs = useMemo(() => {
		const shouldShowBreadcrumbs = isSingleGeoSelected(filters);
		if (!shouldShowBreadcrumbs) {
			return null;
		}

		return formatGeoBreadcrumbs({
			country: country[0],
			region: region[0],
			city: city[0],
		});
	}, [city, country, filters, region]);

	const selectedCity = useMemo(() => {
		if (filters.citySlugs.length === 1 && geo.cities) {
			return city[0];
		}
	}, [city, filters.citySlugs.length, geo.cities]);

	const handleOnInViewChange = useCallback((inView: boolean) => {
		setIsPaxBarInView(inView);
	}, []);

	useEffect(() => {
		// by default 1st page of the lister is set into query
		if ('page' in router.query && parseInt(router.query.page as string) > 1) {
			scroller.scrollTo('result-scroll', {
				duration: 100,
				smooth: true,
			});
		} else if ('topscroll' in router.query) {
			scroller.scrollTo('top-scroll', {
				duration: 100,
				smooth: true,
			});
		}
	}, [router.query]);

	return (
		<>
			<Content className={classes.mainContainer}>
				{formattedBreadCrumbs?.length && (
					<BreadCrumbs breadcrumbs={formattedBreadCrumbs} className={classes.breadCrumbsContainer} />
				)}
				<div className={classes.container}>
					{!filters.isDealsRoute && (
						<>
							<div
								className={cx(classes.filtersContainer, showDialog && classes.filtersContainerVisible)}
							>
								<FiltersWrapper
									showDialog={showDialog}
									toggleDialog={toggleDialog}
									total={totalResults}
									isLoading={isLoadingAccommodations}
								>
									<FilterList
										microCopies={microCopy}
										partyComposition={globalFilters.party}
										setPartyComposition={setPartyComposition}
										setDepartureDate={setDepartureDate}
										departureDate={filters.departureDate}
										setFlexibleDepartureDate={toggleDepartureOffset}
										toggleDepartureOffset={toggleDepartureOffset}
										flexibleDepartureDate={filters.flexibleDepartureDate}
										geo={geo}
										entitySearch={entitySearch}
										onFilterRemove={onFilterRemove}
										durationsRange={durationsRange}
										durationsRangeChange={durationsRangeChange}
										selectedRange={durationsRange}
										onClear={onClear}
										isLoading={isLoadingAccommodations}
									/>
								</FiltersWrapper>
							</div>
						</>
					)}
					<div
						className={cx(classes.resultsContainer, {
							[classes.fullWidth]: filters.isDealsRoute,
						})}
					>
						{brandedSearchTemplate && (
							<>
								<BrandedSearchHeader {...brandedSearchTemplate} readMoreRef={readMoreRef} />
								<Element name={'top-scroll'} />
								{!!brandedSearchTemplate.brandedSearchPages.length && (
									<div>
										<BrandedSearchRelatedCards
											cards={brandedSearchTemplate.brandedSearchPages.slice(
												0,
												MAX_SEARCH_RELATED_CARDS,
											)}
											departureDate={filters.departureDate}
											containerClassName={classes.brandedSearchCards}
											contentClassName={classes.brandedSearchCardsContent}
											cardVariant="image"
											pageType="Search"
										/>
										<Divider className={classes.divider} />
									</div>
								)}
							</>
						)}
						{!brandedSearchTemplate && <Element name={'top-scroll'} />}
						<Element name={'result-scroll'} />

						<div>
							{!filters.isDealsRoute && (
								<>
									{!isDesktop && (
										<>
											<InView
												rootMargin="-72px 0px 0px 0px"
												delay={200}
												initialInView
												onChange={handleOnInViewChange}
											>
												<span className={classes.observeElement} />
											</InView>
											<div
												className={cx(classes.partyPickerFilterBarContainer, {
													[classes.shadow]: !isPaxBarInView,
												})}
											>
												<FilterAndSortBar
													toggleFilter={toggleDialog}
													toggleSort={toggleSortDrawer}
													filterCount={filterCount}
												/>
											</div>
											<Drawer
												open={showSortDrawer}
												title={microCopy['search.sortDrawer.title']}
												onDrawerClose={toggleSortDrawer}
												hasCloseIcon
											>
												<FiltersControls
													microCopies={microCopy}
													filters={filters}
													total={totalResults}
													isLoading={isLoadingAccommodations}
													toggleSortDrawer={toggleSortDrawer}
												/>
											</Drawer>
										</>
									)}
									{isDesktop && (
										<FiltersLabels
											filters={filters}
											onFilterRemove={onFilterRemove}
											onClear={onClear}
											microCopies={microCopy}
											geo={geo}
											isLoading={isLoadingAccommodations}
										/>
									)}
									<div className={classes.filtersControlsWrapper}>
										{isDesktop && (
											<FiltersControls
												microCopies={microCopy}
												filters={filters}
												total={totalResults}
												isLoading={isLoadingAccommodations}
											/>
										)}
									</div>
								</>
							)}
							<div>
								{filters.isDealsRoute ? (
									<FlashDealSearchList />
								) : (
									<AccommodationList
										total={totalResults}
										accommodations={accommodations}
										isLoading={isLoadingAccommodations}
										highlightedAccos={brandedSearchTemplate?.highlightedAccommodations}
									/>
								)}
							</div>
						</div>
					</div>
				</div>
				{brandedSearchTemplate && !brandedSearchTemplate.readMoreLink?.link && (
					<>
						<div ref={readMoreRef} id="read-more" />
						<BrandedSearchFooterBlock {...brandedSearchTemplate} />
					</>
				)}
			</Content>
			{selectedCity && (
				<ReviewContainer
					selectedCity={selectedCity}
					selectedRegion={region[0]}
					selectedCountry={country[0]}
					searchUrl={searchUrl.as.pathname ?? ''}
				/>
			)}
		</>
	);
};

type ReviewsProps = {
	selectedCountry: MappedDestinationView;
	selectedRegion: MappedDestinationView;
	selectedCity: MappedDestinationView;
	searchUrl?: string;
};

const ReviewContainer: FC<ReviewsProps> = ({ selectedCity, selectedRegion, selectedCountry, searchUrl }) => {
	const { reviewsData, fetchNextReviewPage, isLoading } = useGetReviews({ id: selectedCity.id });
	const { dynamicOrigin: origin } = useApplicationStore((s) => s);

	const total = reviewsData?.pages[0].total;
	const reviews = useMemo(() => reviewsData?.pages.flatMap((page) => page.reviews) || [], [reviewsData?.pages]);
	const rating = reviewsData?.pages[0].rating;

	const formattedCitySchemaItem = useMemo(() => {
		const citySchema = getCitySchemaItem(
			selectedCountry.name,
			selectedRegion.name,
			selectedCity.name,
			origin,
			searchUrl,
			total,
			rating,
		);
		if (reviews?.length) {
			Object.assign(citySchema, { review: reviews.map(getReviewSchemaItem) });
		}

		return citySchema;
	}, [origin, rating, reviews, searchUrl, selectedCity.name, selectedCountry.name, selectedRegion.name, total]);

	return (
		<>
			{/* While reviews data is not loaded we should not render structured data
					Otherwise search-engine scrapper will consider FIRST value (without reviews data) as a FINAL value
			 */}
			{!isLoading && <StructuredData schemaItem={formattedCitySchemaItem} id="city-info" isDataDynamic={true} />}
			<Reviews
				type="destination"
				destination={selectedCity}
				reviews={reviews}
				reviewCount={total}
				onLoadMore={fetchNextReviewPage}
			/>
		</>
	);
};
